import { endOfMonth, isBefore, isToday, startOfDay, subDays } from "date-fns";

import { getItemsFromCollection } from "directus/src/operations";
import { useQuery } from "@tanstack/react-query";
import { useCurrencies } from "@/modules/reports/contexts/currency-context";

const calculateStats = (billables, status, convertToINR) => {
  const today = startOfDay(new Date());

  const getExpectedBilling = () => {

    const sum = billables.reduce((sum, b) => {
      const convertedAmount = convertToINR(
        b?.adjusted_amount || b?.amount || 0,
        b.currency
      );
      return sum + convertedAmount;
    }, 0);
    return sum;
  };

  const getOverdueCount = () => {
    return billables.filter((b) => isBefore(new Date(b.billing_date), today))
      .length;
  };

  const getOverdueAmount = () => {

    const dateField =
      {
        billed: "due_date",
        default: "billing_date",
      }[status] || "billing_date";

    const filteredBillables = billables.filter((b) =>
      isBefore(new Date(b[dateField]), today)
    );

    const sum = filteredBillables.reduce((sum, b) => {
      const convertedAmount = convertToINR(
        b?.adjusted_amount || b?.amount || 0,
        b.currency
      );
      return sum + convertedAmount;
    }, 0);
    return sum;
  };

  const getCancelledStats = () => ({
    totalAmount: billables.reduce((sum, b) => {
      const convertedAmount = convertToINR(
        b?.adjusted_amount || b?.amount || 0,
        b.currency
      );
      return sum + convertedAmount;
    }, 0),
    count: billables.length,
  });

  switch (status) {
    case "contracted":
    case "pending_invoice":
      return {
        expectedBilling: getExpectedBilling(),
        overdueCount: getOverdueCount(),
        overdueAmount: getOverdueAmount(),
      };
    case "billed":
      return {
        expectedBilling: getExpectedBilling(),
        overdueAmount: getOverdueAmount(),
      };
    case "collected":
      return {
        collectedBilling: getExpectedBilling(),
      };

    case "cancelled":
      return getCancelledStats();
    case "all":
      return {
        expectedBilling: getExpectedBilling(),
      };
    default:
      return {};
  }
};

export const useBillableStats = (status, filters) => {
  const { convertToINR } = useCurrencies();

  return useQuery({
    queryKey: ["billable-stats", status, filters],
    queryFn: async () => {
      const billablesQuery = {
        fields: [
          "amount",
          "adjusted_amount",
          "currency",
          "status",
          "billing_date",
          "due_date",
          "collected_date",
          "invoice_date",
        ],
        filter: {
          ...(filters ? filters : {}),
          ...(status !== "all" && { status: { _eq: status } }),
        },
        limit: -1,
      };
      const billablesResponse = await getItemsFromCollection(
        "billables",
        billablesQuery
      );

      const stats = calculateStats(billablesResponse, status, convertToINR);

      return stats;
    },
  });
};
